import React, { useState, useRef } from 'react';
import { GoogleGenAI, Type } from "@google/genai";

interface GreenCardCalculatorPageProps {
  language: string;
  onBack: () => void;
  onHome: () => void;
}

const translations: Record<string, any> = {
  en: {
    title: "Green Card Timeline",
    categoryLabel: "Visa Category",
    countryLabel: "Country of Birth",
    pdLabel: "Priority Date",
    pdPlaceholder: "YYYY / MM / DD",
    pathLabel: "Processing Path",
    pathAOS: "Adjustment (I-485)",
    pathCP: "Consular (NVC)",
    calculate: "Analyze Timeline",
    calculating: "Analyzing Trends...",
    results: "Timeline Summary",
    waitBulletin: "Phase 1: Priority Date Wait",
    waitAgency: "Phase 2: Agency Processing",
    totalWait: "Estimated Green Card Date",
    optimistic: "Optimistic Date",
    conservative: "Conservative Date",
    months: "months",
    years: "years",
    disclaimer: "Estimates are for illustrative purposes based on 2025 Visa Bulletin trends and 80% USCIS processing averages.",
    backToHub: "Back",
    recalculate: "Edit Details"
  },
  zh: {
    title: "绿卡时间计算器",
    categoryLabel: "签证类型",
    countryLabel: "出生地",
    pdLabel: "优先日 (Priority Date)",
    pdPlaceholder: "年 / 月 / 日",
    pathLabel: "处理路径",
    pathAOS: "境内调整 (I-485)",
    pathCP: "境外领馆 (NVC)",
    calculate: "分析排期时间",
    calculating: "趋势分析中...",
    results: "时间线摘要",
    waitBulletin: "第一阶段：排期等待 (Table A)",
    waitAgency: "第二阶段：行政处理 (USCIS/NVC)",
    totalWait: "预计拿到绿卡日期",
    optimistic: "乐观估计日期",
    conservative: "保守估计日期",
    months: "个月",
    years: "年",
    disclaimer: "估算结果基于2025年排期趋势及USCIS官方80%案件完成耗时，仅供参考。",
    backToHub: "返回",
    recalculate: "修改信息"
  }
};

const GreenCardCalculatorPage: React.FC<GreenCardCalculatorPageProps> = ({ language, onBack, onHome }) => {
  const t = translations[language] || translations.en;
  const isRTL = language === 'ar';
  
  const [view, setView] = useState<'form' | 'result'>('form');
  const [category, setCategory] = useState('eb1'); 
  const [country, setCountry] = useState('china');
  const [priorityDate, setPriorityDate] = useState(''); 
  const [displayDate, setDisplayDate] = useState(''); 
  const [path, setPath] = useState('aos');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);

  const dateInputRef = useRef<HTMLInputElement>(null);

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    
    let formatted = value;
    if (value.length > 4) {
      formatted = value.slice(0, 4) + ' / ' + value.slice(4);
    }
    if (value.length > 6) {
      formatted = formatted.slice(0, 9) + ' / ' + value.slice(6);
    }
    
    setDisplayDate(formatted);

    if (value.length === 8) {
      const y = value.slice(0, 4);
      const m = value.slice(4, 6);
      const d = value.slice(6, 8);
      setPriorityDate(`${y}-${m}-${d}`);
    } else {
      setPriorityDate('');
    }
  };

  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value; 
    if (!val) return;
    setPriorityDate(val);
    const parts = val.split('-');
    setDisplayDate(`${parts[0]} / ${parts[1]} / ${parts[2]}`);
  };

  const analyzeTimeline = async () => {
    if (!priorityDate) return;
    setLoading(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const prompt = `Act as a senior US Immigration Data Analyst. Analyze the green card timeline for:
        - Category: ${category}
        - Country: ${country}
        - Priority Date (PD): ${priorityDate}
        - Path: ${path === 'aos' ? 'I-485 Adjustment of Status' : 'NVC Consular Processing'}
        
        Logic Requirements:
        1. Phase 1 (Wait for PD): Compare PD with latest 2025 Visa Bulletin Table A (Final Action Dates).
        2. Phase 2 (Processing): Use USCIS 80% case completion averages for 2024/2025 or NVC/Consulate interview averages.
        3. Result: Calculate two specific dates (YYYY-MM-DD) for Optimistic and Conservative scenarios.
        4. Insight: A professional one-sentence insight in ${language === 'zh' ? 'Chinese' : 'English'}.
        
        Respond in JSON:
        {
          "bulletinWaitMonths": number,
          "agencyProcessingMonths": number,
          "optimisticDate": "YYYY-MM-DD",
          "conservativeDate": "YYYY-MM-DD",
          "insight": string
        }`;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          responseMimeType: 'application/json',
          temperature: 0,
          seed: 42,
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              bulletinWaitMonths: { type: Type.NUMBER },
              agencyProcessingMonths: { type: Type.NUMBER },
              optimisticDate: { type: Type.STRING },
              conservativeDate: { type: Type.STRING },
              insight: { type: Type.STRING }
            },
            required: ["bulletinWaitMonths", "agencyProcessingMonths", "optimisticDate", "conservativeDate", "insight"]
          }
        }
      });

      const data = JSON.parse(response.text || "{}");
      setResults(data);
      setView('result');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderHeader = () => (
    <header className="relative z-50 py-6 md:py-10 bg-white/80 backdrop-blur-md sticky top-0 border-b border-slate-50/50">
      <div className="max-w-2xl mx-auto px-6 w-full flex items-center justify-between">
        <button onClick={onHome} className="flex items-center gap-2 active:scale-95 transition-transform group">
          <span className="material-symbols-outlined text-primary text-[26px] font-bold">flight_takeoff</span>
          <span className="font-google-sans font-bold text-[19px] tracking-tight text-slate-dark group-hover:text-primary transition-colors">Settlo</span>
        </button>
        <button 
          onClick={onBack}
          className="flex items-center gap-1 px-4 py-1.5 rounded-full bg-slate-50 border border-slate-100 text-slate-soft hover:text-primary transition-all active:scale-95"
        >
          <span className="text-[13px] font-bold tracking-tight">{t.backToHub}</span>
          <span className={`material-symbols-outlined text-[18px] ${isRTL ? '' : 'rotate-180'}`}>arrow_forward</span>
        </button>
      </div>
    </header>
  );

  if (view === 'result' && results) {
    return (
      <div className={`relative min-h-screen bg-white font-google-sans-text overflow-x-hidden ${isRTL ? 'rtl' : 'ltr'}`}>
        <style>{`.tabular-nums { font-variant-numeric: tabular-nums; }`}</style>
        {renderHeader()}
        
        <main className="relative z-10 max-w-2xl mx-auto px-6 pb-24 text-left animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="mb-6">
            <h1 className="text-[34px] md:text-[42px] font-google-sans font-bold text-slate-dark tracking-tight leading-tight mb-1 uppercase">
              {category.toUpperCase()} <span className="text-primary">{country.toUpperCase()}</span>
            </h1>
            <p className="text-slate-soft text-[10px] font-bold uppercase tracking-[0.2em] opacity-50">{t.results}</p>
          </div>

          <div className="bg-white border border-slate-100 rounded-[24px] overflow-hidden shadow-xl shadow-slate-200/40">
            {/* Phase Breakdown */}
            <div className="px-8 py-8 md:px-10 md:py-10 space-y-6">
              <div className="text-[10px] text-slate-soft/40 font-bold uppercase tracking-[0.25em] mb-2">{language === 'zh' ? '时间线分解' : 'Timeline Breakdown'}</div>
              <div className="space-y-5">
                <div className="flex items-start justify-between">
                  <span className="text-slate-soft font-medium text-[15px] md:text-[16px] tracking-tight pr-4 leading-snug">
                    {t.waitBulletin}
                  </span>
                  <span className="text-[16px] md:text-[18px] font-bold tabular-nums tracking-tight whitespace-nowrap shrink-0 text-slate-dark">
                    +{results.bulletinWaitMonths} {t.months}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-slate-soft font-medium text-[15px] md:text-[16px] tracking-tight pr-4 leading-snug">
                    {t.waitAgency}
                  </span>
                  <span className="text-[16px] md:text-[18px] font-bold tabular-nums tracking-tight whitespace-nowrap shrink-0 text-slate-dark">
                    +{results.agencyProcessingMonths} {t.months}
                  </span>
                </div>
              </div>
            </div>

            {/* Final ETA Dates */}
            <div className="px-8 py-10 md:px-10 md:py-12 bg-primary/[0.02] border-t border-slate-50">
              <div className="space-y-6">
                <div className="text-[11px] text-primary font-bold uppercase tracking-[0.4em]">{t.totalWait}</div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1.5 p-5 bg-white rounded-2xl border border-slate-50 shadow-sm">
                    <div className="text-[10px] text-slate-soft/60 font-bold uppercase tracking-widest">{t.optimistic}</div>
                    <div className="text-[28px] md:text-[34px] font-google-sans font-bold text-slate-dark tracking-tight leading-none tabular-nums">
                      {results.optimisticDate}
                    </div>
                  </div>
                  <div className="space-y-1.5 p-5 bg-white rounded-2xl border border-slate-50 shadow-sm opacity-60">
                    <div className="text-[10px] text-slate-soft/60 font-bold uppercase tracking-widest">{t.conservative}</div>
                    <div className="text-[28px] md:text-[34px] font-google-sans font-bold text-slate-dark tracking-tight leading-none tabular-nums">
                      {results.conservativeDate}
                    </div>
                  </div>
                </div>
                
                <div className="pt-8 mt-4 border-t border-slate-100/60">
                  <p className="text-[14px] md:text-[16px] text-slate-dark font-medium leading-relaxed tracking-tight italic">
                    {results.insight}
                  </p>
                </div>

                <div className="flex justify-center pt-2">
                  <button 
                    onClick={() => setView('form')}
                    className="px-6 py-2 rounded-full border border-slate-200 text-slate-soft text-[11px] font-bold hover:bg-white hover:text-primary hover:border-primary/30 transition-all active:scale-95 uppercase tracking-wider"
                  >
                    {t.recalculate}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <footer className="mt-12 text-center max-w-sm mx-auto">
            <p className="text-[10px] text-slate-soft/30 font-bold tracking-[0.15em] leading-relaxed uppercase">
              {t.disclaimer}
            </p>
          </footer>
        </main>
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen bg-white font-google-sans-text overflow-x-hidden ${isRTL ? 'rtl' : 'ltr'}`}>
      <style>{`
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        select { 
          -webkit-appearance: none !important; 
          appearance: none !important; 
          background-image: none !important;
        }
      `}</style>

      {renderHeader()}

      <main className="relative z-10 max-w-2xl mx-auto px-6 pb-24 text-left animate-in fade-in duration-300">
        <div className="mb-12">
          <h1 className="text-[38px] md:text-[48px] font-google-sans font-bold text-slate-dark tracking-tight leading-tight">{t.title}</h1>
        </div>

        <div className="space-y-6 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em]">{t.categoryLabel}</label>
              <div className="relative">
                <select 
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none cursor-pointer pr-12"
                >
                  <optgroup label="Employment Based">
                    <option value="eb1">EB-1</option>
                    <option value="eb2">EB-2</option>
                    <option value="eb3">EB-3</option>
                    <option value="eb5">EB-5</option>
                  </optgroup>
                  <optgroup label="Family Based">
                    <option value="f1">F-1</option>
                    <option value="f2a">F-2A</option>
                    <option value="f2b">F-2B</option>
                    <option value="f3">F-3</option>
                    <option value="f4">F-4</option>
                  </optgroup>
                </select>
                <span className={`material-symbols-outlined absolute ${isRTL ? 'left-5' : 'right-5'} top-1/2 -translate-y-1/2 text-slate-soft/50 pointer-events-none`}>expand_more</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em]">{t.countryLabel}</label>
              <div className="relative">
                <select 
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none cursor-pointer pr-12"
                >
                  <option value="china">China (Mainland)</option>
                  <option value="india">India</option>
                  <option value="mexico">Mexico</option>
                  <option value="philippines">Philippines</option>
                  <option value="rest">Rest of the World</option>
                </select>
                <span className={`material-symbols-outlined absolute ${isRTL ? 'left-5' : 'right-5'} top-1/2 -translate-y-1/2 text-slate-soft/50 pointer-events-none`}>expand_more</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em]">{t.pdLabel}</label>
            <div className="relative">
              <input 
                type="text"
                inputMode="numeric"
                value={displayDate}
                onChange={handleDateInput}
                placeholder={t.pdPlaceholder}
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 text-[16px] font-bold text-slate-dark focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all outline-none"
              />
              <div className={`absolute ${isRTL ? 'left-5' : 'right-5'} top-1/2 -translate-y-1/2 flex items-center`}>
                <span 
                  className="material-symbols-outlined text-slate-soft/50 cursor-pointer hover:text-primary transition-colors"
                  onClick={() => dateInputRef.current?.showPicker()}
                >
                  calendar_today
                </span>
                <input 
                  ref={dateInputRef}
                  type="date"
                  onChange={handleNativeDateChange}
                  className="absolute opacity-0 pointer-events-none w-0 h-0"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-slate-soft/60 text-[10px] font-bold uppercase tracking-[0.15em]">{t.pathLabel}</label>
            <div className="inline-flex p-1.5 bg-slate-100 rounded-2xl w-full">
              <button 
                onClick={() => setPath('aos')} 
                className={`flex-1 px-6 py-3.5 rounded-xl font-bold text-[14px] transition-all duration-300 ${path === 'aos' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'}`}
              >
                {t.pathAOS}
              </button>
              <button 
                onClick={() => setPath('cp')} 
                className={`flex-1 px-6 py-3.5 rounded-xl font-bold text-[14px] transition-all duration-300 ${path === 'cp' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'}`}
              >
                {t.pathCP}
              </button>
            </div>
          </div>

          <button 
            onClick={analyzeTimeline}
            disabled={loading || !priorityDate}
            className="w-full py-5 bg-indigo-600 text-white font-google-sans font-bold text-[17px] rounded-2xl shadow-stripe-button disabled:opacity-50 transition-all hover:bg-indigo-700 active:scale-[0.98] mt-4"
          >
            {loading ? t.calculating : t.calculate}
          </button>
        </div>
      </main>
    </div>
  );
};

export default GreenCardCalculatorPage;